import React from "react";
import {
  Box,
  Typography,
  Card,
  Chip,
  Button,
  Grid,
  Stack,
  LinearProgress,
} from "@mui/material";
import {
  LocationOn,
  InfoOutlined,
  ArrowForward,
  Home,
  Business,
  Apartment,
  CalendarMonth,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { mapMyInvestmentRow } from "./myInvestmentsMapper";
import noImgPlaceholder from "../../Assets/no-img-placeholder.png";
import { cleanLocation } from "../../utils/location";

const PURPLE = "#7c3aed";
const PURPLE_DARK = "#6d28d9";
const PURPLE_SOFT = "#f5f3ff";
const BLUE_BOX = "#e8f4fc";

function formatMoney(n) {
  const x = typeof n === "number" ? n : Number(n);
  if (!Number.isFinite(x)) return "—";
  return new Intl.NumberFormat("en-SA", { maximumFractionDigits: 2 }).format(x);
}

function formatTokens(n) {
  const x = typeof n === "number" ? n : Number(n);
  if (!Number.isFinite(x)) return "0";
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 4 }).format(x);
}

function formatPayoutDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString("en-SA", { year: "numeric", month: "short", day: "numeric" });
}

function PropertyTypeIcon({ propertyType }) {
  const t = String(propertyType || "").toLowerCase();
  if (t.includes("commercial")) return <Business sx={{ fontSize: 16 }} />;
  if (t.includes("residential")) return <Home sx={{ fontSize: 16 }} />;
  return <Apartment sx={{ fontSize: 16 }} />;
}

function stakeProgressPct(m) {
  const owned = Number(m.tokensOwned);
  const supply = Number(m.totalSupply);
  if (Number.isFinite(owned) && Number.isFinite(supply) && supply > 0) {
    return Math.min(100, Math.max(0, (owned / supply) * 100));
  }
  return 100;
}

export default function InvestorMyInvestmentCard({ row }) {
  const navigate = useNavigate();
  const m = mapMyInvestmentRow(row);
  if (!m) return null;

  const statusLabel = m.statusType || m.assetStatus || "Active";
  const isActive = String(statusLabel).toLowerCase().includes("active");
  const categoryPill =
    m.categoryName || m.propertyType || m.category || "Asset";
  // const totalReturnNum = Number(m.totalReturn);
  // const returnPositive = Number.isFinite(totalReturnNum) && totalReturnNum > 0;
  // const returnNegative = Number.isFinite(totalReturnNum) && totalReturnNum < 0;

  return (
    <Card
      sx={{
        borderRadius: 3,
        overflow: "hidden",
        boxShadow: "0 4px 24px rgba(15, 23, 42, 0.08)",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "#fff",
      }}
    >
      <Box
        sx={{
          position: "relative",
          height: 200,
          bgcolor: "#e2e8f0",
          backgroundImage: `url(${m.mainImageUrl || noImgPlaceholder})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          sx={{
            position: "absolute",
            top: 12,
            left: 12,
            flexWrap: "wrap",
            gap: 0.5,
          }}
        >
          <Chip
            label={statusLabel}
            size="small"
            sx={{
              bgcolor: PURPLE,
              color: "#fff",
              fontWeight: 700,
              fontSize: 11,
              height: 26,
            }}
          />
        </Stack>
        <Chip
          icon={<PropertyTypeIcon propertyType={categoryPill} />}
          label={categoryPill}
          size="small"
          sx={{
            position: "absolute",
            bottom: 12,
            left: 12,
            bgcolor: "rgba(255,255,255,0.95)",
            fontWeight: 600,
            fontSize: 12,
            border: "1px solid rgba(0,0,0,0.06)",
            "& .MuiChip-icon": { ml: "6px" },
          }}
        />
      </Box>

      <Box sx={{ p: 2.5, flex: 1, display: "flex", flexDirection: "column" }}>
        <Typography
          fontWeight={800}
          fontSize={18}
          color="#0f172a"
          sx={{ mb: 0.75, lineHeight: 1.3 }}
        >
          {m.assetName || "—"}
        </Typography>
        {cleanLocation(m.location) ? (
          <Stack direction="row" alignItems="flex-start" spacing={0.75} sx={{ mb: 2 }}>
            <LocationOn sx={{ fontSize: 18, color: "#94a3b8", mt: 0.15 }} />
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45 }}>
              {cleanLocation(m.location)}
            </Typography>
          </Stack>
        ) : null}

        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid item xs={6}>
            {/* <Box sx={{ bgcolor: BLUE_BOX, borderRadius: 2, p: 1.5, height: "100%" }}>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
                display="block"
                sx={{ mb: 0.5 }}
              >
                Total return
              </Typography>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <TrendingUp
                  sx={{
                    fontSize: 18,
                    color: returnNegative ? "#dc2626" : returnPositive ? "#16a34a" : "#64748b",
                  }}
                />
                <Typography
                  fontWeight={800}
                  fontSize={18}
                  sx={{
                    color: returnNegative ? "#dc2626" : returnPositive ? "#16a34a" : "#0f172a",
                  }}
                >
                  {Number.isFinite(totalReturnNum)
                    ? `${totalReturnNum > 0 ? "+ " : totalReturnNum < 0 ? "- " : ""}SAR ${formatMoney(Math.abs(totalReturnNum))}`
                    : "—"}
                </Typography>
              </Stack>
            </Box> */}
          </Grid>
          <Grid item xs={6}>
            <Box sx={{ bgcolor: BLUE_BOX, borderRadius: 2, p: 1.5, height: "100%" }}>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
                display="block"
                sx={{ mb: 0.5 }}
              >
                Current value
              </Typography>
              <Typography fontWeight={800} fontSize={18} color="#0f172a">
                SAR {formatMoney(m.currentValue)}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Box sx={{ mb: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="body2" fontWeight={700} color="#334155">
              My stake
            </Typography>
            <Typography fontWeight={800} fontSize={15} color="#0f172a">
              SAR {formatMoney(m.myStake ?? m.currentValue)}
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={stakeProgressPct(m)}
            sx={{
              height: 8,
              borderRadius: 999,
              bgcolor: "#ede9fe",
              "& .MuiLinearProgress-bar": {
                borderRadius: 999,
                bgcolor: PURPLE,
              },
            }}
          />
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              {formatTokens(m.tokensOwned)} tokens owned
            </Typography>
            <Chip
              label={isActive ? "Active" : statusLabel}
              size="small"
              sx={{
                height: 22,
                fontWeight: 700,
                fontSize: 11,
                bgcolor: PURPLE_SOFT,
                color: PURPLE,
                border: `1px solid ${PURPLE}33`,
              }}
            />
          </Stack>
        </Box>

        <Box
          sx={{
            mb: 2,
            p: 1.5,
            borderRadius: 2,
            bgcolor: PURPLE_SOFT,
            border: "1px solid #ddd6fe",
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" useFlexGap gap={1}>
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                Next payout
              </Typography>
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.25 }}>
                <CalendarMonth sx={{ fontSize: 16, color: "#7c3aed" }} />
                <Typography fontWeight={700} fontSize={14} color="#0f172a">
                  {formatPayoutDate(m.nextPayoutDate)}
                </Typography>
              </Stack>
            </Box>
            <Box sx={{ textAlign: { xs: "left", sm: "right" } }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                Est. amount
              </Typography>
              <Typography fontWeight={800} fontSize={15} color="#0f172a">
                {m.estimatedPayoutAmount != null && Number(m.estimatedPayoutAmount) > 0
                  ? `SAR ${formatMoney(m.estimatedPayoutAmount)}`
                  : "—"}
              </Typography>
            </Box>
          </Stack>
        </Box>

        {/* <Box
          sx={{
            mb: 2,
            p: 1.5,
            borderRadius: 2,
            bgcolor: "#f8fafc",
            border: "1px solid #e2e8f0",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
            <AccountBalanceWallet sx={{ fontSize: 18, color: PURPLE }} />
            <Typography variant="body2" fontWeight={700} color="#334155">
              Your position
            </Typography>
          </Stack>
          <Grid container spacing={1.5}>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                Tokens owned
              </Typography>
              <Typography fontWeight={800} fontSize={16} color="#0f172a">
                {formatTokens(m.tokensOwned)}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                My stake
              </Typography>
              <Typography fontWeight={800} fontSize={16} color="#0f172a">
                SAR {formatMoney(m.myStake ?? m.currentValue)}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                Current value
              </Typography>
              <Typography fontWeight={700} fontSize={15} color="#0f172a">
                SAR {formatMoney(m.currentValue)}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                Total return
              </Typography>
              <Typography
                fontWeight={700}
                fontSize={15}
                sx={{ color: returnNegative ? "#dc2626" : returnPositive ? "#16a34a" : "#0f172a" }}
              >
                SAR {formatMoney(m.totalReturn)}
              </Typography>
            </Grid>
          </Grid>
        </Box> */}

        <Grid container spacing={2} sx={{ mb: 2.5, mt: 0.5 }}>
          <Grid item xs={6}>
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                Price / token
              </Typography>
              <InfoOutlined sx={{ fontSize: 14, color: "#94a3b8" }} />
            </Stack>
            <Typography fontWeight={800} fontSize={15} color="#0f172a">
              SAR {formatMoney(m.pricePerToken)}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ mb: 0.5 }}>
              Investments
            </Typography>
            <Typography fontWeight={800} fontSize={15} color="#0f172a">
              {m.investmentCount != null ? String(m.investmentCount) : String(m.investments.length)}
            </Typography>
          </Grid>
        </Grid>

        <Button
          fullWidth
          variant="contained"
          endIcon={<ArrowForward />}
          onClick={() =>
            m.assetId &&
            navigate(`/investor/viewinvest/${m.assetId}?managed=1`, { state: { viewTab: "my" } })
          }
          sx={{
            mt: "auto",
            textTransform: "none",
            fontWeight: 700,
            py: 1.25,
            borderRadius: 2,
            bgcolor: PURPLE,
            boxShadow: "none",
            "&:hover": { bgcolor: PURPLE_DARK, boxShadow: "none" },
          }}
        >
          Manage Investment
        </Button>
      </Box>
    </Card>
  );
}
